import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Table, Button, Modal, Form, Input, Select, InputNumber, Switch,
  Space, Typography, Tag, Tooltip, App, Breadcrumb,
} from 'antd';
import {
  PlusOutlined, EditOutlined, FileTextOutlined, DeleteOutlined, UnorderedListOutlined, SaveOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAuth } from '../context/AuthContext';
import { coursesApi } from '../api/courses';
import { modulesApi } from '../api/modules';
import { lessonsApi } from '../api/lessons';
import { withServerDetail } from '../api/errorDetail';
import LessonBodyField from '../components/lessonBody/LessonBodyField';
import { BodyDraft, draftFromBody, EMPTY_DRAFT, resolveDraft } from '../components/lessonBody/draft';
import { Course, CourseModule, Lesson, LessonType, Status } from '../api/types';
import { DndContext } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableRow, DragHandle, useSortableSensors } from '../components/SortableTable';
import { reorderByDrag } from '../components/reorder';

const { Title } = Typography;

const STATUS_COLORS: Record<Status, string> = {
  Enabled: 'success',
  Disabled: 'warning',
  Deleted: 'error',
};

const STATUS_OPTIONS: Status[] = ['Enabled', 'Disabled', 'Deleted'];
const TYPE_OPTIONS: LessonType[] = ['Default', 'Practice'];

type LessonFormValues = Omit<Lesson, 'id' | 'moduleId' | 'body'>;

const LessonsPage: React.FC = () => {
  const { courseId, moduleId } = useParams<{ courseId: string; moduleId: string }>();
  const { apiClient } = useAuth();
  const { message } = App.useApp();

  const [course, setCourse] = useState<Course | null>(null);
  const [mod, setMod] = useState<CourseModule | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Lesson | null>(null);
  // Which button is saving: "Save" keeps the modal open, "Save & Close" closes it.
  const [saving, setSaving] = useState<false | 'stay' | 'close'>(false);
  const [bodyDraft, setBodyDraft] = useState<BodyDraft>(EMPTY_DRAFT);
  const [bodyError, setBodyError] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [form] = Form.useForm<LessonFormValues>();
  const lessonType: LessonType | undefined = Form.useWatch('type', form) ?? editing?.type;

  const sensors = useSortableSensors();

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const next = reorderByDrag(lessons, String(active.id), String(over.id));
    if (next === lessons) return;
    setLessons(next);
    lApi()
      .reorder(Number(moduleId), next.map((l) => ({ id: l.id, sortOrder: l.sortOrder })))
      .then((res) => setLessons(res.data.items))
      .catch(() => {
        message.error('Failed to reorder lessons');
        fetchData();
      });
  };

  const cApi = useCallback(() => coursesApi(apiClient!), [apiClient]);
  const mApi = useCallback(() => modulesApi(apiClient!), [apiClient]);
  const lApi = useCallback(() => lessonsApi(apiClient!), [apiClient]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [courseRes, modRes, lessonsRes] = await Promise.all([
        cApi().getById(Number(courseId)),
        mApi().getById(Number(moduleId)),
        lApi().getList(Number(moduleId)),
      ]);
      setCourse(courseRes.data);
      setMod(modRes.data);
      setLessons(lessonsRes.data.items);
    } catch {
      message.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [cApi, mApi, lApi, courseId, moduleId, message]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({ title: '', description: '', status: 'Enabled', sortOrder: lessons.length, isFreePreview: false, type: 'Default' });
    setBodyDraft(EMPTY_DRAFT);
    setBodyError(null);
    setModalOpen(true);
  };

  const openEdit = (lesson: Lesson) => {
    setEditing(lesson);
    form.setFieldsValue({
      title: lesson.title,
      description: lesson.description,
      status: lesson.status,
      sortOrder: lesson.sortOrder,
      isFreePreview: lesson.isFreePreview,
      type: lesson.type,
    });
    // Practice bodies (`{ tasks }`) are edited on their own page, never here.
    const { draft, error } = lesson.type === 'Practice'
      ? { draft: EMPTY_DRAFT, error: null }
      : draftFromBody(lesson.body);
    setBodyDraft(draft);
    setBodyError(error);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setBodyError(null);
  };

  // A ref, not `saving`: a second click or Ctrl/⌘+S can arrive before that state renders.
  const submittingRef = useRef(false);

  const submit = async (close: boolean) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      let values: LessonFormValues;
      try {
        values = await form.validateFields();
      } catch {
        return;
      }

      let body: unknown;
      if (values.type === 'Practice') {
        body = editing?.body ?? { tasks: [] };
      } else {
        const resolved = resolveDraft(bodyDraft);
        if ('error' in resolved) {
          setBodyError(resolved.error);
          message.error('Fix the lesson body before saving — see the editor below.');
          return;
        }
        body = resolved.body;
      }
      setBodyError(null);

      setSaving(close ? 'close' : 'stay');
      try {
        const payload = { ...values, moduleId: Number(moduleId), body };
        const res = editing
          ? await lApi().update({ ...payload, id: editing.id })
          : await lApi().create(payload);
        message.success(editing ? 'Lesson updated' : 'Lesson created');
        if (close) {
          setModalOpen(false);
        } else {
          // A just-created lesson stays open in edit mode, so the next save updates it
          // instead of creating a duplicate.
          setEditing(res.data);
        }
        fetchData();
      } catch (err) {
        message.error(withServerDetail('Failed to save lesson', err));
      }
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  // Ctrl/⌘+S saves without closing while the modal is open.
  const submitRef = useRef(submit);
  submitRef.current = submit;
  useEffect(() => {
    if (!modalOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.code !== 'KeyS') return;
      e.preventDefault();
      // Some inputs commit on blur (a story scene's id) — commit it, then save once that
      // edit has rendered, so the save sees it.
      const active = document.activeElement;
      if (active instanceof HTMLElement && active.hasAttribute('data-commit-on-blur')) active.blur();
      setTimeout(() => submitRef.current(false));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [modalOpen]);

  const handleBatchDelete = () => {
    Modal.confirm({
      title: `Delete ${selectedRowKeys.length} lesson(s)?`,
      content: 'Selected lessons will be marked as Deleted. This can be reverted by editing each lesson.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await lApi().batchDelete(selectedRowKeys.map(Number));
          message.success(`Deleted ${selectedRowKeys.length} lesson(s)`);
          setSelectedRowKeys([]);
          fetchData();
        } catch {
          message.error('Failed to delete lessons');
        }
      },
    });
  };

  const columns: ColumnsType<Lesson> = [
    { title: '', key: 'sort', width: 40, align: 'center', render: () => <DragHandle /> },
    { title: '#', key: 'idx', width: 60, render: (_: unknown, __: Lesson, index: number) => index + 1 },
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: 'Title', dataIndex: 'title' },
    { title: 'Description', dataIndex: 'description', ellipsis: true },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      filters: STATUS_OPTIONS.map(s => ({ text: s, value: s })),
      defaultFilteredValue: ['Enabled', 'Disabled'],
      onFilter: (value, record) => record.status === value,
      render: (s: Status) => <Tag color={STATUS_COLORS[s]}>{s}</Tag>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      width: 110,
      render: (t: LessonType) =>
        t === 'Practice' ? <Tag color="purple">Practice</Tag> : <Tag>Default</Tag>,
    },
    {
      title: 'Body',
      width: 90,
      align: 'center',
      render: (_: any, record: Lesson) => {
        if (record.type === 'Practice') {
          const count = Array.isArray(record.body?.tasks) ? record.body.tasks.length : 0;
          return (
            <Tooltip title={`${count} task(s)`}>
              <Tag icon={<FileTextOutlined />} color="purple">{count}</Tag>
            </Tooltip>
          );
        }
        const hasBody = record.body && Array.isArray(record.body?.cards);
        return hasBody ? (
          <Tooltip title={`${record.body.cards.length} card(s)`}>
            <Tag icon={<FileTextOutlined />} color="blue">{record.body.cards.length}</Tag>
          </Tooltip>
        ) : (
          <Tag color="default">—</Tag>
        );
      },
    },
    {
      title: '',
      width: 130,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit lesson">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          {record.type === 'Practice' && (
            <Link to={`/courses/${courseId}/modules/${moduleId}/lessons/${record.id}/practice`}>
              <Button size="small" icon={<UnorderedListOutlined />}>Tasks</Button>
            </Link>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <Breadcrumb
          items={[
            { title: <Link to="/courses">Courses</Link> },
            { title: <Link to={`/courses/${courseId}/modules`}>{course?.title ?? `Course ${courseId}`}</Link> },
            { title: mod?.title ?? `Module ${moduleId}` },
            { title: 'Lessons' },
          ]}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>
            Lessons{mod ? ` — ${mod.title}` : ''}
          </Title>
          <Space>
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={selectedRowKeys.length === 0}
              onClick={handleBatchDelete}
            >
              Delete selected{selectedRowKeys.length ? ` (${selectedRowKeys.length})` : ''}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Lesson
            </Button>
          </Space>
        </div>

        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <SortableContext items={lessons.map((l) => String(l.id))} strategy={verticalListSortingStrategy}>
            <Table
              columns={columns}
              dataSource={lessons}
              rowKey="id"
              loading={loading}
              rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
              pagination={false}
              components={{ body: { row: SortableRow } }}
            />
          </SortableContext>
        </DndContext>
      </Space>

      <Modal
        title={editing ? 'Edit Lesson' : 'New Lesson'}
        open={modalOpen}
        onCancel={closeModal}
        destroyOnHidden
        // A stray click outside, or Esc meant for a dropdown, must not throw away a long edit.
        mask={{ closable: false }}
        keyboard={false}
        width="90%"
        style={{ top: 24 }}
        footer={[
          <Button key="cancel" onClick={closeModal}>Cancel</Button>,
          <Tooltip key="save" title="Save and keep editing (Ctrl/⌘+S)">
            <Button
              icon={<SaveOutlined />}
              loading={saving === 'stay'}
              disabled={saving === 'close'}
              onClick={() => submit(false)}
            >
              Save
            </Button>
          </Tooltip>,
          <Button
            key="save-close"
            type="primary"
            loading={saving === 'close'}
            disabled={saving === 'stay'}
            onClick={() => submit(true)}
          >
            Save &amp; Close
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 16 }}
          // Practice lessons are created Disabled: a client build that predates practice
          // support crashes on a `{ tasks: [...] }` body, and keeping the lesson Disabled
          // until both apps have shipped is the branch's primary control against that.
          // Type cannot change while editing, so this only ever fires during create.
          onValuesChange={(changed: Partial<LessonFormValues>) => {
            if (editing || changed.type === undefined) return;
            form.setFieldsValue({ status: changed.type === 'Practice' ? 'Disabled' : 'Enabled' });
          }}
        >
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
            <Input placeholder="e.g. Hello World" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Short description of the lesson" />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
            <Form.Item name="status" label="Status" rules={[{ required: true }]}>
              <Select
                options={STATUS_OPTIONS.map(s => ({
                  value: s,
                  label: <Tag color={STATUS_COLORS[s]}>{s}</Tag>,
                }))}
              />
            </Form.Item>
            <Form.Item name="sortOrder" label="Sort Order" rules={[{ required: true, message: 'Required' }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="type" label="Type" rules={[{ required: true }]}>
              <Select
                disabled={!!editing}
                options={TYPE_OPTIONS.map((t) => ({ value: t, label: t }))}
              />
            </Form.Item>
            <Form.Item name="isFreePreview" label="Free preview" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
          {!editing && lessonType === 'Practice' && (
            <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
              Status was set to <strong>Disabled</strong>. App builds that predate practice
              support crash on a practice body, so a practice lesson should stay Disabled
              until both clients have shipped. Switch it to Enabled only then.
            </Typography.Paragraph>
          )}
        </Form>
        {/* Outside <Form>: the body editors render their own inputs and must not nest in its form. */}
        {lessonType === 'Practice' ? (
          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            Practice lessons are edited on their own page — save this lesson, then use
            the Tasks button in the table.
          </Typography.Paragraph>
        ) : (
          <LessonBodyField
            draft={bodyDraft}
            onChange={setBodyDraft}
            error={bodyError}
            onErrorChange={setBodyError}
          />
        )}
      </Modal>
    </>
  );
};

export default LessonsPage;
